import { useState } from 'react';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';

import { FormRow } from '@platform-ui/form';

import { InputPhonePh as BasicInputPhone } from '../../ui/input-phone-ph';
import type { InputPhoneProps as BasicInputProps } from '../../ui/input-phone-ph';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>
  extends
    Omit<BasicInputProps, 'name' | 'onChange' | 'onBlur' | 'value'>,
    UseControllerReturn<TFieldValues, TName> {
  withValidityMark?: boolean;
}

export function InputPhone<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>) {
  const {
    field: { ref, ...field },
    fieldState,
    formState: _formState,
    withValidityMark = true,
    required,
    ...inputProps
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  const hasError = typeof fieldState.error !== 'undefined' && !isEdited;
  const errorMessage = fieldState.error?.message;

  const hasInitialValue = !!field.value && !fieldState.isDirty;
  const hasValidValueWritten =
    !!field.value && fieldState.isTouched && !hasError;

  const shouldShowValidityMark =
    withValidityMark && !isFocused && (hasInitialValue || hasValidValueWritten);

  return (
    <FormRow required={required} error={hasError} errorMessage={errorMessage}>
      <BasicInputPhone
        {...field}
        {...inputProps}
        refCallback={ref}
        onClean={() => field.onChange('')}
        valid={shouldShowValidityMark && !hasError}
        required={required}
        error={hasError}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setIsEdited(false);
          field.onBlur();
        }}
        onChange={(_event, params) => {
          setIsEdited(true);
          field.onChange(params.value);
        }}
      />
    </FormRow>
  );
}
